import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Offcanvas, Form, Button, Alert } from 'react-bootstrap';
import { AuthContext } from '../App';
import Footer from './Footer';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';

function Locations(props) {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [formType, setFormType] = useState(null); // 'province', 'district', or 'village'
    const [editItem, setEditItem] = useState(null); // Item being edited
    const [countries, setCountries] = useState(null);
    const [provinces, setProvinces] = useState(null);
    const [districts, setDistricts] = useState(null);
    const [villages, setVillages] = useState(null);
    const [formProvinces, setFormProvinces] = useState(null);
    const [formDistricts, setFormDistricts] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedVillage, setSelectedVillage] = useState(null);
    const [formData, setFormData] = useState({
        country_id: '',
        adm1: '',
        adm1_id: '',
        adm2: '',
        adm2_id: '',
        adm3: '',
        latitude: '',
        longitude: '',
    });
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFetchingProvinces, setIsFetchingProvinces] = useState(false);
    const [isFetchingDistricts, setIsFetchingDistricts] = useState(false);

    const [provinceSearch, setProvinceSearch] = useState('');
    const [districtSearch, setDistrictSearch] = useState('');
    const [villageSearch, setVillageSearch] = useState('');

    // Filter data based on search
    const filteredProvinces = provinces?.filter((province) =>
        province.adm1.toLowerCase().includes(provinceSearch.toLowerCase())
    );

    const filteredGroupedDistricts = districts
        ? Object.entries(
            districts
                .filter(district =>
                    district.adm2.toLowerCase().includes(districtSearch.toLowerCase()) // Only match district name
                )
                .reduce((acc, district) => {
                    const province = district.adm1;
                    if (!acc[province]) acc[province] = [];
                    acc[province].push(district);
                    return acc;
                }, {})
        )
        : [];

    const filteredGroupedVillages = villages
        ? Object.entries(
            villages
                .filter(village =>
                    village.adm3.toLowerCase().includes(villageSearch.toLowerCase()) // Only match village name
                )
                .reduce((acc, village) => {
                    const province = village.adm1;
                    const district = village.adm2;
                    if (!acc[province]) acc[province] = {};
                    if (!acc[province][district]) acc[province][district] = [];
                    acc[province][district].push(village);
                    return acc;
                }, {})
        )
        : [];

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            window.location.href = '/login';
        }
    }, [isAuthenticated, navigate]);

    // Function to get auth headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');

        const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);

        if (isTokenExpired) {
            logout();
            window.location.href = '/login';
            return {};
        }

        return {
            'Content-Type': 'application/json',
            Authorization: token,
        };
    };

    // Handle API errors
    const handleApiError = (error, response) => {
        if (
            response &&
            response.status === 401 &&
            error.message.includes('Your authentication token is invalid')
        ) {
            logout();
            window.location.href = '/login';
            return 'Session expired. Please log in again.';
        }
        return error.message || 'An error occurred. Please try again.';
    };

    // Fetch countries for main UI
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/locations/country`, {
                    headers: getAuthHeaders(),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setCountries(data.data || []);
                setSelectedCountry(data?.data?.[0]?.country_id || null);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        if (isAuthenticated) {
            fetchCountries();
        }
    }, [isAuthenticated]);

    // Fetch provinces, districts, villages for main UI
    useEffect(() => {
        const fetchProvinces = async () => {
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setProvinces(data.data || []);
                setSelectedProvince(data.data[0]?.adm1_id || null);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        const fetchDistricts = async () => {
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setDistricts(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        const fetchVillages = async () => {
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm3?country_id=${selectedCountry}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setVillages(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        if (selectedCountry && isAuthenticated) {
            fetchProvinces();
            fetchDistricts();
            fetchVillages();
        }
    }, [selectedCountry, isAuthenticated]);

    // Fetch provinces for Offcanvas form
    useEffect(() => {
        const fetchFormProvinces = async () => {
            if (!formData.country_id) {
                setFormProvinces(null);
                setFormDistricts(null);
                setIsFetchingProvinces(false);
                return;
            }
            setIsFetchingProvinces(true);
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm1?country_id=${formData.country_id}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setFormProvinces(data.data || []);
                if (data.data.length === 0) {
                    setFormError('No provinces available for this country.');
                }
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            } finally {
                setIsFetchingProvinces(false);
            }
        };

        if (isAuthenticated && formType && formData.country_id) {
            fetchFormProvinces();
        }
    }, [formData.country_id, isAuthenticated, formType]);

    // Fetch districts for Offcanvas form (village form)
    useEffect(() => {
        const fetchFormDistricts = async () => {
            if (!formData.adm1_id) {
                setFormDistricts(null);
                setIsFetchingDistricts(false);
                return;
            }
            setIsFetchingDistricts(true);
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm2?country_id=${formData.country_id}&adm1_id=${formData.adm1_id}&adm2_id=${formData.adm2_id}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                } const data = await response.json();
                setFormDistricts(Array.isArray(data.data) ? data.data : [data.data]);
                if (data.data.length === 0) {
                    setFormError('No districts available for this province.');
                }
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            } finally {
                setIsFetchingDistricts(false);
            }
        };

        if (isAuthenticated && formType === 'village' && formData.adm1_id) {
            fetchFormDistricts();
        }
    }, [formData.adm1_id, isAuthenticated, formType]);

    const handleClose = () => {
        setShow(false);
        setFormType(null);
        setEditItem(null);
        setFormError(null);
        setFormProvinces(null);
        setFormDistricts(null);
        setIsFetchingProvinces(false);
        setIsFetchingDistricts(false);
        setFormData({
            country_id: '',
            adm1: '',
            adm1_id: '',
            adm2: '',
            adm2_id: '',
            adm3: '',
            latitude: '',
            longitude: '',
        });
    };

    const handleShow = (type, item = null) => {
        setFormType(type);
        setEditItem(item);
        if (item) {
            // Preload form data for editing
            if (type === 'province') {
                setFormData({
                    country_id: selectedCountry,
                    adm1: item.adm1,
                    adm1_id: item.adm1_id.toString(),
                    adm2: '',
                    adm2_id: '',
                    adm3: '',
                    latitude: '',
                    longitude: '',
                });
            } else if (type === 'district') {
                setFormData({
                    country_id: selectedCountry,
                    adm1: '',
                    adm1_id: item.adm1_id.toString(),
                    adm2: item.adm2,
                    adm2_id: item.adm2_id.toString(),
                    adm3: '',
                    latitude: '',
                    longitude: '',
                });
            } else if (type === 'village') {
                setFormData({
                    country_id: selectedCountry,
                    adm1: '',
                    adm1_id: item.adm1_id.toString(),
                    adm2: '',
                    adm2_id: item.adm2_id.toString(),
                    adm3: item.adm3,
                    latitude: item.latitude.toString(),
                    longitude: item.longitude.toString(),
                });
            }
        } else {
            // Reset for adding new
            setFormData({
                country_id: selectedCountry,
                adm1: '',
                adm1_id: '',
                adm2: '',
                adm2_id: '',
                adm3: '',
                latitude: '',
                longitude: '',
            });
        }
        setShow(true);
    };

    const handleDelete = async (type, item) => {
        const itemName = type === 'province' ? item.adm1 : type === 'district' ? item.adm2 : item.adm3;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete ${itemName}? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) {
            return;
        }

        let url, payload;
        if (type === 'province') {
            url = `${envConfig.API_URL}/lookups/locations/adm1`;
            payload = {
                country_id: parseInt(selectedCountry), // item.country_id
                adm1_id: parseInt(item.adm1_id),
            };
        } else if (type === 'district') {
            url = `${envConfig.API_URL}/lookups/locations/adm2`;
            payload = {
                country_id: parseInt(selectedCountry), // item.country_id
                adm1_id: parseInt(item.adm1_id),
                adm2_id: parseInt(item.adm2_id),
            };
        } else if (type === 'village') {
            url = `${envConfig.API_URL}/lookups/locations/adm3`;
            payload = {
                country_id: parseInt(selectedCountry), // item.country_id
                adm1_id: parseInt(item.adm1_id),
                adm2_id: parseInt(item.adm2_id),
                adm3_id: parseInt(item.adm3_id),
            };
        }

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Error: ${response.status}`);
            }

            // Show success message
            await Swal.fire({
                title: 'Deleted!',
                text: `${itemName} has been deleted.`,
                icon: 'success',
                confirmButtonText: 'OK',
            });

            // Refresh main UI data
            if (type === 'province') {
                const provincesResponse = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                const provincesData = await provincesResponse.json();
                setProvinces(provincesData.data || []);
            } else if (type === 'district') {
                const districtsResponse = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                const districtsData = await districtsResponse.json();
                setDistricts(districtsData.data || []);
            } else if (type === 'village') {
                const villagesResponse = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm3?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                const villagesData = await villagesResponse.json();
                setVillages(villagesData.data || []);
            }
        } catch (error) {
            const errorMessage = handleApiError(error, error.response);
            setFormError(errorMessage);
            Swal.fire({
                title: 'Error!',
                text: errorMessage || `Failed to delete ${itemName}.`,
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    const onCountryChange = (e) => {
        setSelectedCountry(e.target.value);
        setProvinces(null);
        setDistricts(null);
        setVillages(null);
        setSelectedProvince(null);
        setSelectedDistrict(null);
        setSelectedVillage(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const newData = { ...prev, [name]: value };
            if (name === 'country_id') {
                newData.adm1_id = '';
                newData.adm2_id = '';
                newData.adm3 = '';
                newData.latitude = '';
                newData.longitude = '';
                setFormProvinces(null);
                setFormDistricts(null);
            } else if (name === 'adm1_id') {
                newData.adm2_id = '';
                newData.adm3 = '';
                newData.latitude = '';
                newData.longitude = '';
                setFormDistricts(null);
            } else if (name === 'adm2_id') {
                newData.adm3 = '';
                newData.latitude = '';
                newData.longitude = '';
            }
            return newData;
        });
        setFormError(null);
    };

    const validateForm = () => {
        if (!formData.country_id) return 'Country is required';
        if (formType === 'province') {
            if (!formData.adm1) return 'Province name is required';
        }
        if (formType === 'district') {
            if (!formData.adm1_id) return 'Please choose a province';
            if (!formData.adm2) return 'District name is required';
        }
        if (formType === 'village') {
            if (!formData.adm1_id) return 'Please choose a province';
            if (!formData.adm2_id) return 'Please choose a district';
            if (!formData.adm3) return `${selectedCountry == 1 ? 'EPA' : 'Camp'} name is required'`;
            if (!formData.latitude) return 'Latitude is required';
            if (!formData.longitude) return 'Longitude is required';
            if (isNaN(formData.latitude) || isNaN(formData.longitude))
                return 'Latitude and Longitude must be valid numbers';
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        setIsSubmitting(true);

        const error = validateForm();
        if (error) {
            setFormError(error);
            setIsSubmitting(false);
            return;
        }

        let url, payload, method;
        if (formType === 'province') {
            url = `${envConfig.API_URL}/lookups/locations/adm1`;
            method = editItem ? 'PATCH' : 'POST';
            payload = {
                country_id: parseInt(formData.country_id),
                adm1: formData.adm1,
                ...(editItem && { adm1_id: parseInt(editItem.adm1_id) }),
            };
        } else if (formType === 'district') {
            url = `${envConfig.API_URL}/lookups/locations/adm2`;
            method = editItem ? 'PATCH' : 'POST';
            payload = {
                country_id: parseInt(formData.country_id),
                adm1_id: parseInt(formData.adm1_id),
                adm2: formData.adm2,
                ...(editItem && { adm2_id: parseInt(editItem.adm2_id) }),
            };
        } else if (formType === 'village') {
            url = `${envConfig.API_URL}/lookups/locations/adm3`;
            method = editItem ? 'PATCH' : 'POST';
            payload = {
                country_id: parseInt(formData.country_id),
                adm1_id: parseInt(formData.adm1_id),
                adm2_id: parseInt(formData.adm2_id),
                adm3: formData.adm3,
                latitude: parseFloat(formData.latitude),
                longitude: parseFloat(formData.longitude),
                ...(editItem && { adm3_id: parseInt(editItem.adm3_id) }),
            };
        }

        try {
            const response = await fetch(url, {
                method,
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            // Handle custom API-level failure
            if (!response.ok || result.success === 0) {
                throw new Error(result.message || `Error: ${response.status}`);
            }

            // Show success message
            const displayName = formType === 'village' ? selectedCountry == 1 ? 'EPA' : 'Camp' : formType.charAt(0).toUpperCase() + formType.slice(1);
            await Swal.fire({
                title: editItem ? 'Updated!' : 'Saved!',
                text: `${displayName} ${editItem ? 'updated' : 'added'} successfully.`,
                icon: 'success',
                confirmButtonText: 'OK',
            });

            // Refresh main UI data
            if (formType === 'province') {
                const provincesResponse = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                const provincesData = await provincesResponse.json();
                setProvinces(provincesData.data || []);
            } else if (formType === 'district') {
                const districtsResponse = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                const districtsData = await districtsResponse.json();
                setDistricts(districtsData.data || []);
            } else if (formType === 'village') {
                const villagesResponse = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm3?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                const villagesData = await villagesResponse.json();
                setVillages(villagesData.data || []);
            }

            handleClose();
        } catch (error) {
            const message = error.message || 'Failed to save the location.';
            setFormError(message);

            Swal.fire({
                title: 'Error!',
                text: message,
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Group districts by province (adm1)
    const groupedDistricts = districts
        ? districts.reduce((acc, district) => {
            const province = district.adm1;
            if (!acc[province]) {
                acc[province] = [];
            }
            acc[province].push(district);
            return acc;
        }, {})
        : {};

    // Group villages by province (adm1) and district (adm2)
    const groupedVillages = villages
        ? villages.reduce((acc, village) => {
            const province = village.adm1;
            const district = village.adm2;
            if (!acc[province]) {
                acc[province] = {};
            }
            if (!acc[province][district]) {
                acc[province][district] = [];
            }
            acc[province][district].push(village);
            return acc;
        }, {})
        : {};

    const handleCoordinateChange = (e) => {
        const { name, value } = e.target;
        const regex = /^-?\d{0,3}(\.\d{0,8})?$/;
        if (value === '' || value === '-' || regex.test(value)) {
            const floatVal = parseFloat(value);
            if (name === 'latitude') {
                if (!isNaN(floatVal) && (floatVal < -90 || floatVal > 90)) {
                    return;
                }
            } else if (name === 'longitude') {
                if (!isNaN(floatVal) && (floatVal < -180 || floatVal > 180)) {
                    return;
                }
            }
            setFormData((prev) => ({ ...prev, [name]: value }));
            setFormError((prev) => ({ ...prev, [name]: '' }));
        }
    };

    return (
        <>
            <div className="wrapper pt-100px">
                <div className="container-fluid">
                    <div className="row pt-3">
                        <div className="col-sm-12 col-md-12 col-lg-12">
                            <div className="box-config">
                                <h5 className="mt-3">location</h5>
                                <div className="d-flex">
                                    <div className="form-group">
                                        <label htmlFor="">Country</label>
                                        <select
                                            className="form-select custom-select"
                                            value={selectedCountry || ''}
                                            onChange={onCountryChange}
                                            style={{ width: '250px' }}
                                        >
                                            {countries &&
                                                countries.map((country) => (
                                                    <option key={country.country_id} value={country.country_id}>
                                                        {country.country}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                </div>

                                <Tabs
                                    defaultActiveKey="Province"
                                    id="uncontrolled-tab-example"
                                    className="mb-3 mt-3"
                                >
                                    <Tab eventKey="Province" title="Province">
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 col-lg-12">
                                                <div className="tab-one">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h5 className="">Currently Available</h5>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex">
                                                                <div className="searchBox">
                                                                    <input
                                                                        placeholder="Search Provinces"
                                                                        className="form-control"
                                                                        type="text"
                                                                        value={provinceSearch}
                                                                        onChange={(e) => setProvinceSearch(e.target.value)}
                                                                    />
                                                                    <span>
                                                                        <img alt="" src="./assets/images/search.svg" />
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <button
                                                                        className="btn btn-new ms-2 pe-2"
                                                                        onClick={() => handleShow('province')}
                                                                    >
                                                                        <span>
                                                                            <img
                                                                                alt=""
                                                                                src="./assets/images/add.svg"
                                                                                style={{ height: '21px' }}
                                                                            />
                                                                        </span>
                                                                        Add New
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="table-responsive">
                                                    <table className="table tbl_allData two">
                                                        <thead>
                                                            <tr>
                                                                <th className="w-85" rowSpan={2}>
                                                                    Province
                                                                </th>
                                                                <th className="">Options</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredProvinces?.length > 0 ? (
                                                                filteredProvinces.map((province, index) => (
                                                                    <tr key={index}>
                                                                        <td>{province.adm1}</td>
                                                                        <td>
                                                                            <button
                                                                                className="btn btn-default me-2 p-0"
                                                                                onClick={() => handleShow('province', province)}
                                                                            >
                                                                                <img src="./assets/images/edit.svg" alt="Edit" />
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-default p-0"
                                                                                onClick={() => handleDelete('province', province)}
                                                                            >
                                                                                <img src="./assets/images/delete.svg" alt="Delete" />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="2">No provinces found.</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </Tab>
                                    <Tab eventKey="District" title="District">
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 col-lg-12">
                                                <div className="tab-one">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h5 className="">Currently Available</h5>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex">
                                                                <div className="searchBox">
                                                                    <input
                                                                        placeholder="Search Districts"
                                                                        className="form-control"
                                                                        type="text"
                                                                        value={districtSearch}
                                                                        onChange={(e) => setDistrictSearch(e.target.value)}
                                                                    />
                                                                    <span>
                                                                        <img alt="" src="./assets/images/search.svg" />
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <button
                                                                        className="btn btn-new ms-2 pe-2"
                                                                        onClick={() => handleShow('district')}
                                                                    >
                                                                        <span>
                                                                            <img
                                                                                alt=""
                                                                                src="./assets/images/add.svg"
                                                                                style={{ height: '21px' }}
                                                                            />
                                                                        </span>
                                                                        Add New
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="table-responsive">
                                                    <table className="table tbl_allData two">
                                                        <thead>
                                                            <tr>
                                                                <th>Province</th>
                                                                <th>District</th>
                                                                <th>Options</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredGroupedDistricts.length > 0 ? (
                                                                filteredGroupedDistricts.map(([provinceName, districtList]) =>
                                                                    districtList.map((district, index) => (
                                                                        <tr key={district.adm2_id}>
                                                                            {index === 0 && <td rowSpan={districtList.length}>{provinceName}</td>}
                                                                            <td>{district.adm2}</td>
                                                                            <td>
                                                                                <button
                                                                                    className="btn btn-default me-2 p-0"
                                                                                    onClick={() => handleShow('district', district)}
                                                                                >
                                                                                    <img src="./assets/images/edit.svg" alt="Edit" />
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-default p-0"
                                                                                    onClick={() => handleDelete('district', district)}
                                                                                >
                                                                                    <img src="./assets/images/delete.svg" alt="Delete" />
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="3">No districts found.</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </Tab>
                                    <Tab eventKey="Villages" title={selectedCountry == 1 ? 'EPAs' : 'Camps'}>
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 col-lg-12">
                                                <div className="tab-one">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <h5 className="">Currently Available</h5>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex">
                                                                <div className="searchBox">
                                                                    <input
                                                                        placeholder={`Search ${selectedCountry == 1 ? 'EPAs' : 'Camps'}`}
                                                                        className="form-control"
                                                                        type="text"
                                                                        value={villageSearch}
                                                                        onChange={(e) => setVillageSearch(e.target.value)}
                                                                    />
                                                                    <span>
                                                                        <img alt="" src="./assets/images/search.svg" />
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <button
                                                                        className="btn btn-new ms-2 pe-2"
                                                                        onClick={() => handleShow('village')}
                                                                    >
                                                                        <span>
                                                                            <img
                                                                                alt=""
                                                                                src="./assets/images/add.svg"
                                                                                style={{ height: '21px' }}
                                                                            />
                                                                        </span>
                                                                        Add New
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="table-responsive">
                                                    <table className="table tbl_allData two">
                                                        <thead>
                                                            <tr>
                                                                <th>Province</th>
                                                                <th>District</th>
                                                                <th>{selectedCountry == 1 ? 'EPA' : 'Camp'}</th>
                                                                <th>Coordinates</th>
                                                                <th>Options</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredGroupedVillages.length > 0 ? (
                                                                filteredGroupedVillages.flatMap(([province, districts]) => {
                                                                    const provinceRowSpan = Object.values(districts).reduce(
                                                                        (sum, camps) => sum + camps.length,
                                                                        0
                                                                    );
                                                                    let isFirstProvinceRow = true;

                                                                    return Object.entries(districts).flatMap(([district, camps]) => {
                                                                        const districtRowSpan = camps.length;
                                                                        let isFirstDistrictRow = true;

                                                                        const rows = camps.map((village, index) => {
                                                                            const row = (
                                                                                <tr key={village.adm3_id}>
                                                                                    {isFirstProvinceRow && <td rowSpan={provinceRowSpan}>{province}</td>}
                                                                                    {isFirstDistrictRow && <td rowSpan={districtRowSpan}>{district}</td>}
                                                                                    <td>{village.adm3}</td>
                                                                                    <td>
                                                                                        {`${village.latitude}, ${village.longitude}`}
                                                                                    </td>
                                                                                    <td>
                                                                                        <button
                                                                                            className="btn btn-default me-2 p-0"
                                                                                            onClick={() => handleShow('village', village)}
                                                                                        >
                                                                                            <img src="./assets/images/edit.svg" alt="Edit" />
                                                                                        </button>
                                                                                        <button
                                                                                            className="btn btn-default p-0"
                                                                                            onClick={() => handleDelete('village', village)}
                                                                                        >
                                                                                            <img src="./assets/images/delete.svg" alt="Delete" />
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );

                                                                            isFirstDistrictRow = false;
                                                                            if (index === 0) isFirstProvinceRow = false;

                                                                            return row;
                                                                        });

                                                                        return rows;
                                                                    });
                                                                })
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="5">No {selectedCountry == 1 ? 'EPAs' : 'Camps'} found.</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </Tab>
                                </Tabs>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            <Offcanvas show={show} onHide={handleClose} placement="end" {...props}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        {editItem
                            ? `Edit ${formType === 'province'
                                ? 'Province'
                                : formType === 'district'
                                    ? 'District'
                                    : selectedCountry == 1
                                        ? 'EPA'
                                        : 'Camp'
                            }`
                            : `Add New ${formType === 'province'
                                ? 'Province'
                                : formType === 'district'
                                    ? 'District'
                                    : selectedCountry == 1
                                        ? 'EPA'
                                        : 'Camp'
                            }`}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form onSubmit={handleSubmit}>
                        {/* {formError && <Alert variant="danger">{formError}</Alert>} */}

                        {/* Country Dropdown */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Country <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                name="country_id"
                                value={formData.country_id}
                                onChange={handleFormChange}
                                required
                                disabled={true}
                            >
                                <option value="">Select</option>
                                {countries &&
                                    countries.map((country) => (
                                        <option key={country.country_id} value={country.country_id}>
                                            {country.country}
                                        </option>
                                    ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Province Fields */}
                        {formType === 'province' && (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Province Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="adm1"
                                    value={formData.adm1}
                                    onChange={handleFormChange}
                                    required
                                />
                            </Form.Group>
                        )}

                        {/* District and Village Fields */}
                        {(formType === 'district' || formType === 'village') && (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Province <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="adm1_id"
                                    value={formData.adm1_id}
                                    onChange={handleFormChange}
                                    required
                                    disabled={
                                        !formData.country_id ||
                                        isFetchingProvinces ||
                                        !formProvinces ||
                                        (editItem && formType === 'district') ||
                                        (editItem && formType === 'village')
                                    }
                                >
                                    <option value="">Select</option>
                                    {formProvinces &&
                                        formProvinces.map((province) => (
                                            <option key={province.adm1_id} value={province.adm1_id}>
                                                {province.adm1}
                                            </option>
                                        ))}
                                </Form.Select>
                                {isFetchingProvinces && <Form.Text>Loading provinces...</Form.Text>}
                                {!isFetchingProvinces &&
                                    formData.country_id &&
                                    formProvinces?.length === 0 && (
                                        <Form.Text className="text-danger">
                                            No provinces available for this country.
                                        </Form.Text>
                                    )}
                            </Form.Group>
                        )}

                        {/* District Fields */}
                        {formType === 'district' && (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    District Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="adm2"
                                    value={formData.adm2}
                                    onChange={handleFormChange}
                                    required
                                />
                            </Form.Group>
                        )}

                        {/* Village Fields */}
                        {formType === 'village' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        District <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="adm2_id"
                                        value={formData.adm2_id}
                                        onChange={handleFormChange}
                                        required
                                        disabled={
                                            !formData.adm1_id ||
                                            isFetchingDistricts ||
                                            !formDistricts ||
                                            (editItem && formType === 'village')
                                        }
                                    >
                                        <option value="">Select</option>
                                        {formDistricts &&
                                            formDistricts.map((district) => (
                                                <option key={district.adm2_id} value={district.adm2_id}>
                                                    {district.adm2}
                                                </option>
                                            ))}
                                    </Form.Select>
                                    {isFetchingDistricts && <Form.Text>Loading districts...</Form.Text>}
                                    {!isFetchingDistricts &&
                                        formData.adm1_id &&
                                        formDistricts?.length === 0 && (
                                            <Form.Text className="text-danger">
                                                No districts available for this province.
                                            </Form.Text>
                                        )}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {selectedCountry == 1 ? 'EPA' : 'Camp'} Name <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="adm3"
                                        value={formData.adm3}
                                        onChange={handleFormChange}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Latitude <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="latitude"
                                        value={formData.latitude}
                                        onChange={handleCoordinateChange}
                                        required
                                        title="Latitude must be a number between -90 and 90"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Longitude <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="longitude"
                                        value={formData.longitude}
                                        onChange={handleCoordinateChange}
                                        required
                                        title="Longitude must be a number between -180 and 180"
                                    />
                                </Form.Group>
                            </>
                        )}

                        <div className="row pt-5">
                            <div className="col-sm-12 col-md-6 col-lg-6">
                                <button
                                    // variant="secondary"
                                    className="w-100 btn btn-cancel"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="col-sm-12 col-md-6 col-lg-6">
                                <button
                                    // variant="primary"
                                    type="submit"
                                    className="w-100 btn btn-submit"
                                    disabled={
                                        isSubmitting ||
                                        isFetchingProvinces ||
                                        isFetchingDistricts ||
                                        (formType !== 'province' && formProvinces?.length === 0) ||
                                        (formType === 'village' && formDistricts?.length === 0)
                                    }
                                >
                                    {isSubmitting ? 'Saving...' : editItem ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

export default Locations;